import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import type { AddReportTagModel } from '../components/AddEdit/AddReportTag';
import { AppRoutes } from '../helper/appRoutes';
import useAuth from './AuthProvider';
import { apiClient } from '../helper/api';

const EXTRACTION_POLL_MS = 5000;

interface ExtractionContextType {
    extractedValues: AddReportTagModel[];
    setExtractedValues: React.Dispatch<React.SetStateAction<AddReportTagModel[]>>;
    extractionInProgress: boolean;
    extractionComplete: boolean;
    extractionTimedOut: boolean;
    setExtractionInProgress: (val: boolean) => void;
    setExtractionComplete: (val: boolean) => void;
    onExtractionSuccess: (values: AddReportTagModel[]) => void;
    onExtractionError: () => void;
    onExtractionTimeout: (extractionId: string) => void;
    clearExtraction: () => void;
    setIsOnReportTagPage: (val: boolean) => void;
}

const ExtractionContext = createContext<ExtractionContextType | undefined>(undefined);

export const ExtractionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [extractedValues, setExtractedValues] = useState<AddReportTagModel[]>([]);
    const [extractionInProgress, setExtractionInProgress] = useState(false);
    const [extractionComplete, setExtractionComplete] = useState(false);
    const [extractionTimedOut, setExtractionTimedOut] = useState(false);
    const [pendingExtractionId, setPendingExtractionId] = useState<string | null>(null);
    const isOnReportTagPageRef = useRef(false);
    const [isOnReportTagPage, setIsOnReportTagPageState] = useState(false);
    const extractionInProgressRef = useRef(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const tokenRef = useRef(user?.token);
    useEffect(() => { tokenRef.current = user?.token; }, [user?.token]);

    useEffect(() => {
        extractionInProgressRef.current = extractionInProgress;
    }, [extractionInProgress]);

    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (extractionInProgressRef.current) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    // Poll for extraction results while the user waits on the page after a timeout.
    useEffect(() => {
        if (!extractionTimedOut || !pendingExtractionId) return;
        const token = tokenRef.current;
        if (!token) return;

        const intervalId = window.setInterval(() => {
            apiClient(`/ReportTags/getPendingTagExtraction?extractionId=${pendingExtractionId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                }),
            }).then(async (responseJson) => {
                if (!responseJson?.status || !responseJson?.data) return;
                const { records, partnersId, stateId, fileName, pdfUrl } = responseJson.data;
                let pdfFiles: File[] = [];
                if (pdfUrl && fileName) {
                    try {
                        const pdfResp = await fetch(pdfUrl);
                        const pdfBlob = await pdfResp.blob();
                        pdfFiles = [new File([pdfBlob], `${fileName}.pdf`, { type: 'application/pdf' })];
                    } catch { /* non-fatal — submit without PDF */ }
                }
                const mappedValues: AddReportTagModel[] = (records ?? []).map((record: any) => {
                    const raw = record?.dueDate ?? '';
                    const s = String(raw).trim();
                    let dueDateStr = '';
                    if (s) {
                        const isoCandidate = s.split('T')[0].slice(0, 10);
                        if (/^\d{4}-\d{2}-\d{2}$/.test(isoCandidate)) {
                            dueDateStr = isoCandidate;
                        } else {
                            const us = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
                            if (us) dueDateStr = `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
                        }
                    }
                    return {
                        partnersId: partnersId ?? 0,
                        stateId: stateId ?? 0,
                        numberOfTags: 'single',
                        invoiceNumber: record?.invoiceNumber ?? '',
                        insuredName: record?.insuredName ?? '',
                        policyNumber: record?.policyNumber ?? '',
                        tagNumber: Number(record?.tagNumber),
                        errorTagCodes: record?.errorTagCodeIds ?? [],
                        filerNotes: record?.filerNotes ?? '',
                        dueDate: dueDateStr,
                        files: pdfFiles,
                    };
                });
                onExtractionSuccess(mappedValues);
            }).catch(() => { /* non-fatal — keep polling */ });
        }, EXTRACTION_POLL_MS);

        return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extractionTimedOut, pendingExtractionId]);

    const onExtractionTimeout = (extractionId: string) => {
        setExtractionInProgress(false);
        setExtractionTimedOut(true);
        setPendingExtractionId(extractionId);
    };

    const onExtractionSuccess = (values: AddReportTagModel[]) => {
        setExtractedValues(values);
        setExtractionInProgress(false);
        setExtractionComplete(true);
        setExtractionTimedOut(false);
        setPendingExtractionId(null);
        if (!isOnReportTagPageRef.current) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Extraction complete',
                text: 'Click to review the extracted data.',
                showConfirmButton: true,
                confirmButtonText: 'Go to Report Tags',
                confirmButtonColor: '#2099d2',
                width: '300px',
                padding: '0.6em',
                timer: 10000,
                timerProgressBar: true,
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate(AppRoutes.home.reportTag);
                }
            });
        }
    };

    const onExtractionError = () => {
        setExtractionInProgress(false);
    };

    const clearExtraction = () => {
        setExtractedValues([]);
        setExtractionInProgress(false);
        setExtractionComplete(false);
        setExtractionTimedOut(false);
        setPendingExtractionId(null);
    };

    const setIsOnReportTagPage = (val: boolean) => {
        isOnReportTagPageRef.current = val;
        setIsOnReportTagPageState(val);
    };

    // Show when extraction is running but the user has navigated away from the page.
    // Rendered directly in the provider so no Swal is involved — other pages firing
    // their own Swal dialogs won't knock this out.
    const showLoadingBanner = (extractionInProgress || extractionTimedOut) && !isOnReportTagPage;

    return (
        <ExtractionContext.Provider value={{
            extractedValues,
            setExtractedValues,
            extractionInProgress,
            extractionComplete,
            extractionTimedOut,
            setExtractionInProgress,
            setExtractionComplete,
            onExtractionSuccess,
            onExtractionError,
            onExtractionTimeout,
            clearExtraction,
            setIsOnReportTagPage,
        }}>
            {children}
            {showLoadingBanner && (
                <div style={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 99999,
                    background: '#0d4a63',
                    color: '#fff',
                    borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13,
                    fontWeight: 500,
                    minWidth: 220,
                }}>
                    <svg width="18" height="18" viewBox="0 0 24 24"
                        style={{ animation: 'extraction-spin 1s linear infinite', flexShrink: 0 }}
                    >
                        <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" fill="none" />
                        <path d="M12 2 a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" />
                    </svg>
                    <span>Extraction in progress…</span>
                    <style>{`@keyframes extraction-spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}
        </ExtractionContext.Provider>
    );
};

export const useExtractionContext = () => {
    const context = useContext(ExtractionContext);
    if (!context) throw new Error('useExtractionContext must be used within ExtractionProvider');
    return context;
};
